import { styled } from '@material-ui/core';

import { down } from '../../../assets/style/breakpointHelpers';
import { domTag } from '../styledFilter';
import type { LocationType } from './types';

export const ModalOverlay = styled('div')({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	backgroundColor: 'var(--color-bg-overlay)',
	backdropFilter: 'blur(8px)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 1050,
	animation: 'modalFadeIn 0.2s ease-out',
});

export const ModalContainer = styled('div')({
	backgroundColor: 'var(--color-bg-elevated)',
	borderRadius: 'var(--radius-xl)',
	boxShadow: 'var(--shadow-xl)',
	width: '40%',
	minWidth: '500px',
	maxWidth: '800px',
	maxHeight: '90vh',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	animation: 'modalScaleIn 0.2s ease-out',
});

export const ModalHeader = styled('div')({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '16px 20px',
	borderBottom: '1px solid var(--color-border-subtle)',
	gap: '12px',
	[down('sm')]: {
		padding: '12px 16px',
	},
});

export const HeaderLeft = styled('div')({
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
	flex: 1,
	minWidth: 0,
});

const getBadgeColor = (type: LocationType) => {
	switch (type) {
		case 'LGA':
			return {
				bg: 'var(--color-warning-light)',
				text: 'var(--color-warning-dark)',
			};
		case 'Ward':
			return {
				bg: 'var(--color-info-light)',
				text: 'var(--color-info-dark)',
			};
		case 'Polling Unit':
			return {
				bg: 'var(--color-success-light)',
				text: 'var(--color-success-dark)',
			};
		default:
			return {
				bg: 'var(--color-bg-subtle)',
				text: 'var(--color-text-secondary)',
			};
	}
};

export const LocationTypeBadge = styled(domTag('span', ['locationtype']))(({ locationtype }: { locationtype: LocationType }) => {
	const colors = getBadgeColor(locationtype);
	return {
		backgroundColor: colors.bg,
		color: colors.text,
		padding: '4px 10px',
		borderRadius: 'var(--radius-md)',
		fontSize: '11px',
		fontFamily: 'var(--Inter-semibold)',
		textTransform: 'uppercase',
		whiteSpace: 'nowrap',
		letterSpacing: '0.5px',
	};
});

export const LocationName = styled('h2')({
	fontSize: '16px',
	fontFamily: 'var(--Inter-semibold)',
	color: 'var(--color-text-primary)',
	margin: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	[down('sm')]: {
		fontSize: '14px',
	},
});

export const CloseButton = styled('button')({
	'background': 'transparent',
	'border': 'none',
	'padding': '8px',
	'borderRadius': 'var(--radius-md)',
	'cursor': 'pointer',
	'display': 'flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'color': 'var(--color-text-secondary)',
	'transition': 'background-color var(--transition-fast), color var(--transition-fast)',
	'flexShrink': 0,
	'&:hover': {
		backgroundColor: 'var(--color-bg-subtle)',
		color: 'var(--color-text-primary)',
	},
	'&:focus': {
		outline: '2px solid var(--color-brand-primary)',
		outlineOffset: '2px',
	},
});

export const ModalBody = styled('div')({
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	gap: '20px',
	padding: '20px',
	overflow: 'auto',
	[down('md')]: {
		gridTemplateColumns: '1fr',
		gap: '16px',
		padding: '16px',
	},
});

export const InfoSection = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
	[down('md')]: {
		order: 2,
	},
});

export const InfoRow = styled('div')({
	display: 'flex',
	flexDirection: 'column',
	gap: '4px',
});

export const InfoLabel = styled('span')({
	fontSize: '11px',
	fontFamily: 'var(--Inter-medium)',
	color: 'var(--color-text-secondary)',
	textTransform: 'uppercase',
	letterSpacing: '0.5px',
});

export const InfoValue = styled('span')({
	fontSize: '14px',
	fontFamily: 'var(--Inter-regular)',
	color: 'var(--color-text-primary)',
	wordBreak: 'break-word',
});

export const MapSection = styled('div')({
	[down('md')]: {
		order: 1,
	},
});

export const MapContainer = styled('div')({
	height: '280px',
	borderRadius: 'var(--radius-lg)',
	overflow: 'hidden',
	border: '1px solid var(--color-border-subtle)',
	[down('md')]: {
		height: '200px',
	},
});

export const MapPlaceholder = styled('div')({
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '12px',
	backgroundColor: 'var(--color-bg-subtle)',
	color: 'var(--color-text-tertiary)',
	padding: '20px',
	textAlign: 'center',
});

export const MapPlaceholderIcon = styled('div')({
	fontSize: '32px',
	opacity: 0.6,
});

export const MapPlaceholderText = styled('p')({
	margin: 0,
	fontSize: '13px',
	fontFamily: 'var(--Inter-regular)',
	lineHeight: 1.5,
});

export const ModalFooter = styled('div')({
	display: 'flex',
	justifyContent: 'flex-end',
	padding: '16px 20px',
	borderTop: '1px solid var(--color-border-subtle)',
	[down('sm')]: {
		padding: '12px 16px',
	},
});

export const DismissButton = styled('button')({
	'backgroundColor': 'var(--color-bg-subtle)',
	'color': 'var(--color-text-primary)',
	'border': '1px solid var(--color-border-default)',
	'padding': '10px 24px',
	'borderRadius': 'var(--radius-md)',
	'fontSize': '14px',
	'fontFamily': 'var(--Inter-medium)',
	'cursor': 'pointer',
	'transition': 'background-color var(--transition-fast), border-color var(--transition-fast)',
	'&:hover': {
		backgroundColor: 'var(--color-bg-hover)',
		borderColor: 'var(--color-border-hover)',
	},
	'&:focus': {
		outline: '2px solid var(--color-brand-primary)',
		outlineOffset: '2px',
	},
	[down('sm')]: {
		width: '100%',
	},
});

export const ClickableLabel = styled('span')({
	'cursor': 'pointer',
	'color': 'var(--color-text-primary)',
	'transition': 'color var(--transition-fast)',
	'textDecoration': 'underline',
	'textDecorationColor': 'transparent',
	'textUnderlineOffset': '2px',
	'&:hover': {
		color: 'var(--color-brand-primary)',
		textDecorationColor: 'var(--color-brand-primary)',
	},
	'&:focus': {
		outline: '2px solid var(--color-brand-primary)',
		outlineOffset: '2px',
		borderRadius: '2px',
	},
});
